import { memo, useMemo } from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import styles from './Table.module.scss';

const Table = ({
  columns,
  rows,
  onRowClick,
  onCellClick,
  stickyHeader,
  classNames,
}) => {
  const gridStyles = useMemo(
    () => ({
      gridTemplateColumns: columns.reduce(
        (acc, { width, minWidth = 'min-content' }) =>
          `${acc} ${width ?? `minmax(${minWidth}, 1fr)`}`,
        '',
      ),
    }),
    [columns],
  );

  return (
    <div className={cn(styles.root, classNames?.root)} data-testid="table-root">
      <div className={styles.body}>
        <div
          className={cn(styles.head, classNames?.head, {
            [styles.sticky]: stickyHeader,
          })}
          style={gridStyles}
          data-testid="table-head"
        >
          {columns.map(({ id, label }) => (
            <div
              key={id}
              className={cn(
                styles.cell,
                styles.cellHead,
                classNames?.cell,
                classNames?.cellHead,
              )}
              data-testid="table-cell"
            >
              {label}
            </div>
          ))}
        </div>
        {rows.map((row, index) => (
          <div
            key={row.id ?? index}
            onClick={() => onRowClick?.(row)}
            className={cn(styles.row, classNames?.row, {
              [styles.clickable]: onRowClick && !onCellClick,
              [styles.disabled]: row._disabled,
            })}
            style={gridStyles}
            data-testid="table-row"
          >
            {columns.map((column) => {
              const cellContent = row[column.id];

              return (
                <div
                  key={column.id}
                  onClick={(e) => {
                    if (onCellClick) {
                      e.stopPropagation();
                      cellContent && onCellClick(cellContent, row, column.id);
                    }
                  }}
                  className={cn(
                    styles.cell,
                    styles.cellRow,
                    classNames?.cell,
                    classNames?.cellRow,
                    {
                      [styles.clickable]: cellContent && onCellClick,
                    },
                  )}
                  data-testid="table-cell"
                >
                  {cellContent ?? '-'}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      width: PropTypes.string,
      minWidth: PropTypes.string,
    }),
  ).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      _disabled: PropTypes.bool,
    }),
  ).isRequired,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  stickyHeader: PropTypes.bool,
  classNames: PropTypes.shape({
    root: PropTypes.string,
    head: PropTypes.string,
    row: PropTypes.string,
    cell: PropTypes.string,
    cellHead: PropTypes.string,
    cellRow: PropTypes.string,
  }),
};

export default memo(Table);
